import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Send, Lock, BookOpen, GraduationCap, FileX } from 'lucide-react';
import { jobApplicationApi } from '@/lib/api/jobApplicationApi';
import { prerequisiteApi, RequiredModulesStatus } from '@/lib/api/prerequisiteApi';
import { onboardingApi } from '@/lib/api/onboardingApi';
import { PublicNeed } from '@/lib/models/jobApplication';
import { OnboardingProgram } from '@/lib/models/onboarding';
import { LoadingState, EmptyState } from '@/components/custom-ui/empty-state';
import { ActionButton } from '@/components/custom-ui/action-button';
import NeedView, { publicNeedToView } from '@/components/needs/NeedView';
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_SPACING } from '@/lib/nexilink-card-styles';
import ApplicationModal from './ApplicationModal';

const JobDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [need, setNeed] = useState<PublicNeed | null>(null);
    const [reqStatus, setReqStatus] = useState<RequiredModulesStatus | null>(null);
    const [onboardingProgram, setOnboardingProgram] = useState<OnboardingProgram | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [hasApplied, setHasApplied] = useState(false);

    useEffect(() => { if (id) loadNeed(id); }, [id]);

    const loadNeed = async (needId: string) => {
        setIsLoading(true);
        try {
            // Viewer route: active postings OR one this candidate applied to, so an
            // applied posting stays viewable after the employer unpublishes/closes it (#276).
            const data = await jobApplicationApi.getNeedForViewer(needId);
            setNeed(data);

            if (data.onboarding_program_id) {
                onboardingApi.getProgramById(data.onboarding_program_id).then(setOnboardingProgram).catch(() => {});
            }
            // Course-based prerequisites (gating)
            try {
                const status = await prerequisiteApi.getRequiredModulesStatus(needId);
                if (status.total > 0) setReqStatus(status);
            } catch { /* may be unauthenticated */ }
        } catch {
            // The viewer route (#276) returns this posting for active postings OR
            // ones this candidate applied to. A 404 here means it's genuinely gone
            // for them — a draft/removed posting they never applied to, or one the
            // employer hard-deleted (Radera). Show a clear "no longer available"
            // state instead of silently bouncing to the job board (which read like
            // a broken 404 for an applied candidate).
            setNotFound(true);
        } finally {
            setIsLoading(false);
        }
    };

    const hasPrereqs = !!reqStatus && reqStatus.total > 0;
    const canApply = !hasPrereqs || !!reqStatus?.all_met;

    if (isLoading) return <LoadingState message="Laddar tjänst..." />;

    if (notFound || !need) {
        return (
            <div className={NEXILINK_SPACING}>
                <button onClick={() => navigate('/dashboard/jobs')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
                    <ArrowLeft className="w-4 h-4" /> <span className="text-sm font-medium">Tillbaka till lediga tjänster</span>
                </button>
                <EmptyState
                    icon={FileX}
                    title="Tjänsten är inte längre tillgänglig"
                    description="Annonsen kan ha tagits bort eller avpublicerats. Om du har sökt tjänsten hittar du den under Mina ansökningar."
                    action={{ label: 'Till lediga tjänster', icon: ArrowLeft, onClick: () => navigate('/dashboard/jobs') }}
                />
            </div>
        );
    }

    return (
        <div className={NEXILINK_SPACING}>
            <button onClick={() => navigate('/dashboard/jobs')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
                <ArrowLeft className="w-4 h-4" /> <span className="text-sm font-medium">Tillbaka till lediga tjänster</span>
            </button>

            <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-6 md:p-8`}>
                <NeedView
                    data={publicNeedToView(need)}
                    footer={
                        <div className="pt-5 border-t-2 border-gray-100">
                            {hasApplied ? (
                                <div className="flex items-center gap-2 text-green-600"><CheckCircle className="w-5 h-5" /><span className="font-medium">Din ansökan har skickats!</span></div>
                            ) : canApply ? (
                                <ActionButton icon={Send} onClick={() => setShowApplyModal(true)}>Ansök till denna tjänst</ActionButton>
                            ) : (
                                <button disabled className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-500 rounded-xl cursor-not-allowed font-medium">
                                    <Lock className="w-4 h-4" /> Genomför förkunskapskraven först
                                </button>
                            )}
                        </div>
                    }
                >
                    {/* Role onboarding (informational) */}
                    {onboardingProgram && (
                        <div className="p-4 rounded-xl bg-[#A4C3B2]/20 border border-[#A4C3B2]">
                            <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-[#1A4D4F]" /> Onboarding i denna roll</h3>
                            <p className="text-sm text-gray-700">
                                {onboardingProgram.name}
                                {onboardingProgram.module_ids?.length ? ` · ${onboardingProgram.module_ids.length} moduler` : ''}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Detta program tilldelas dig om du anställs för rollen.</p>
                        </div>
                    )}

                    {/* Course prerequisites (gating) */}
                    {hasPrereqs && (
                        <div>
                            <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2"><BookOpen className="w-5 h-5" /> Förkunskapskrav</h3>
                            <div className="space-y-2">
                                {reqStatus!.modules.map((m) => (
                                    // Clickable so the candidate can LAUNCH the required course inline
                                    // (#36 — the job page told them to "complete the prerequisite" but
                                    // gave no path to it; they had to hunt for it under Moduler). Both
                                    // met and unmet rows open the module (review vs complete).
                                    <button
                                        key={m.module_id}
                                        type="button"
                                        onClick={() => navigate(`/dashboard/modules/${m.module_id}`)}
                                        className={`group w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${m.met ? 'bg-green-50 hover:bg-green-100' : 'bg-amber-50 hover:bg-amber-100'}`}
                                    >
                                        {m.met ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" /> : <Lock className="w-5 h-5 text-amber-500 flex-shrink-0" />}
                                        <span className={`text-sm font-medium ${m.met ? 'text-green-700' : 'text-amber-700'}`}>{m.module_name || 'Modul'}</span>
                                        <span className={`ml-auto text-xs font-medium ${m.met ? 'text-green-600' : 'text-amber-700'}`}>
                                            {m.met ? 'Genomförd' : 'Starta modul →'}
                                        </span>
                                    </button>
                                ))}
                            </div>
                            {!canApply && <p className="mt-3 text-sm text-amber-600">Du behöver genomföra alla förkunskapskrav innan du kan ansöka. Klicka på en modul ovan för att börja.</p>}
                        </div>
                    )}
                </NeedView>
            </div>

            {showApplyModal && (
                <ApplicationModal
                    needId={need.id}
                    needTitle={need.role}
                    onClose={() => setShowApplyModal(false)}
                    onSuccess={() => { setShowApplyModal(false); setHasApplied(true); }}
                />
            )}
        </div>
    );
};

export default JobDetailPage;
